/**
 * Everything that moves, in one object: the conductor and its clock, the
 * camera, the origins registry, a renderer (WebGL2, or Canvas2D), kinetic
 * type, sound, and the degrade ladder.
 *
 * This file is the adapter to the page. It owns the requestAnimationFrame
 * loop (render on demand; the bit-sea breathes at 30 fps while idle and
 * sleeps after 20 s), the DOM overlay the conductor's slips and sentences
 * live in, the resize and theme observers, the hidden-tab rules, and the
 * swap to Canvas2D when WebGL2 is missing, too slow, or lost. It decides
 * nothing about what a beat looks like; the conductor does.
 */

import type { Conductor, CreateFx, Cue, FixIt, FrameModel, Fx, Ghost, Origin, Origins, Unsub } from '../contracts';
import { h } from '../../../console/src/dom';
import { CAM, createCamera, type CameraImpl } from './camera';
import { createCanvas2dRenderer } from './canvas2d';
import { createConductor, type ConductorImpl, type OverlayContent } from './conductor';
import type { FxScene } from './frame';
import { CLIMB_AFTER_MS, Governor, LADDER, createGlRenderer, type FxRenderer, type Quality } from './gl';
import { readTheme, viewerHue, type Theme } from './palette';
import { CardLift, LIFT_SCALE, SPRINGS, Spring2, magnetTilt, springSettled, springStep, velocityLean, type SpringState } from './tween';
import { createSound, type Sound } from './sound';
import { createTypist, type Typist } from './type';

export const IDLE_SLEEP_MS = 20_000;
export const IDLE_FRAME_MS = 33;          // the idle bit-sea's 30 fps
export const LOST_GRACE_MS = 3000;        // a lost context gets this long to come back before Canvas2D
export const PARALLAX_PX = 6;
export const PAN_PARALLAX = 0.2;
/** Reduced motion: a dive, an unrolling ring or an exploding stack is a crossfade this long (§4.6). */
export const CROSSFADE_MS = 150;

/** The origins registry: stage modules say where things are; beats start and land there. */
export const createOrigins = (): Origins => {
  const table = new Map<string, (() => Origin)[]>();
  return {
    register(id, at): Unsub {
      const list = table.get(id) ?? [];
      list.push(at);
      table.set(id, list);
      return () => {
        const l = table.get(id);
        if (!l) return;
        const i = l.lastIndexOf(at);
        if (i >= 0) l.splice(i, 1);
        if (l.length === 0) table.delete(id);
      };
    },
    at(id) {
      const l = table.get(id);
      const fn = l?.[l.length - 1];
      if (!fn) return null;
      try {
        const o = fn();
        return o && Number.isFinite(o.x) && Number.isFinite(o.y) ? o : null;
      } catch {
        return null;
      }
    },
  };
};

/** URL switches: `?gl=0` forces Canvas2D, `?perf=1` shows the frame HUD (in the query or the hash's query). */
export const readSwitches = (loc: { search: string; hash: string }): { gl0: boolean; perf: boolean } => {
  const q = new URLSearchParams(loc.search);
  const hq = loc.hash.includes('?') ? new URLSearchParams(loc.hash.slice(loc.hash.indexOf('?') + 1)) : null;
  const get = (k: string) => q.get(k) ?? hq?.get(k) ?? null;
  return { gl0: get('gl') === '0', perf: get('perf') === '1' };
};

export type { Ghost } from '../contracts';

/**
 * What fx offers beyond the contract, for its own tests and the harness: the camera's cut hook,
 * a magnetic card (≤ 6° lean on the card spring, --fx-mx / --fx-my for the sheen; off under
 * reduced motion), and which renderer is drawing. The ghost's springs are k 320 / c 26 (home
 * k 520 / c 30); a lifted card adds 1.04 scale and a lean on k 420 / c 32.
 */
export interface FxImpl extends Fx {
  readonly camera: CameraImpl;
  magnetic(el: HTMLElement): Unsub;
  readonly renderer: 'gl' | 'canvas2d' | null;
  /** A fix-it chosen on a refusal's sentence (its button's click): told to every `onFixIt` listener. */
  chooseFixIt(f: FixIt, at: Origin, via: 'click' | 'keyboard'): void;
}

/** A sentence may take focus unless a sheet holds it or someone is typing. */
const mayTakeFocus = (): boolean => {
  if (typeof document === 'undefined') return false;
  const a = document.activeElement as HTMLElement | null;
  if (a?.closest?.('[aria-modal="true"], .ring-menu')) return false;
  const tag = a?.tagName;
  return !(tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || a?.isContentEditable);
};

export const createFx: CreateFx = (opts) => {
  const origins = createOrigins();
  const camera = createCamera();
  let reduced = opts.reduced;
  let reducedByUser = false;
  let theme: Theme | null = null;
  let renderer: FxRenderer | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let overlayRoot: HTMLElement | null = null;
  let layer: HTMLElement | null = null;
  let layerOrigin = { x: 0, y: 0 };
  let stage = { left: 0, top: 0, width: 1280, height: 800 };
  let glAllowed = !opts.forceCanvas2d;
  let forced = !!opts.forceCanvas2d;   // Canvas2D by request (?gl=0), not by the ladder: no eco badge
  let perfHud = opts.perfHud ?? false;
  let destroyed = false;
  let raf = 0;
  let needsDraw = true;
  let lastActivity = 0;
  let lastDraw = 0;
  let lastFull: number | null = null;
  let seaTime = 0;
  let lastSea = 0;
  let cursor = { x: 0, y: 0 };
  let lostTimer: ReturnType<typeof setTimeout> | null = null;
  let wakeTimer: ReturnType<typeof setTimeout> | null = null;
  const governor = new Governor({ max: 5 });
  const qualityFns = new Set<(q: number) => void>();
  const fixFns = new Set<(f: FixIt, at: Origin, via: 'click' | 'keyboard') => void>();
  const cleanups: (() => void)[] = [];
  const sound: Sound = createSound();
  sound.setOn(false);
  let soundWanted = opts.sound;
  const typist: Typist = createTypist({ reduced: () => reduced });

  const dpr = () => (governor.level >= 4 ? 1 : Math.min(2, Math.max(1, globalThis.devicePixelRatio || 1)));

  const conductor: ConductorImpl = createConductor({
    origins, camera, now: () => performance.now(), dpr, stageRect: () => stage,
  });
  cleanups.push(conductor.onSound((e) => sound.play(e)));
  conductor.setReduced(reduced);
  camera.setReduced(reduced);

  // ---------- things that follow a hand: ghosts and magnetic cards ----------

  interface Mover { step(dtMs: number): boolean; snap(): void }   // step: true while still moving
  const movers = new Set<Mover>();
  let lastStep = 0;
  const stepMovers = (t: number): boolean => {
    const dt = lastStep ? Math.min(64, Math.max(0, t - lastStep)) : 16;
    lastStep = t;
    let moving = false;
    for (const m of movers) if (m.step(dt)) moving = true;
    return moving;
  };

  const ghost = (el: HTMLElement, opts: { card?: boolean } = {}): Ghost => {
    const spring = new Spring2(0, 0, SPRINGS.ghost);
    const lift = opts.card ? new CardLift() : null;
    let target = { x: 0, y: 0 };
    let last = { x: 0, y: 0 };
    let active = false;
    let landed: (() => void) | null = null;
    if (lift) el.classList?.add('fx-lifted');
    const write = (x: number, y: number, lean: number, scale = lift ? LIFT_SCALE : 1) => {
      el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) rotate(${lean.toFixed(2)}deg)${lift ? ` scale(${scale.toFixed(3)})` : ''}`;
    };
    const land = () => {
      active = false;
      el.style.willChange = '';
      lift?.rest();
      write(target.x, target.y, 0);
      const done = landed;
      landed = null;
      done?.();
    };
    const mover: Mover = {
      step(dt) {
        if (!active) return false;
        const p = spring.step(target.x, target.y, dt);
        const vx = ((p.x - last.x) / Math.max(1, dt)) * 1000;
        last = p;
        if (lift) {
          const pose = lift.step(vx, dt);
          write(p.x, p.y, pose.lean, pose.scale);
          if (spring.settled(target.x, target.y) && lift.settled()) { land(); return false; }
          return true;
        }
        write(p.x, p.y, velocityLean(vx));
        if (spring.settled(target.x, target.y)) { land(); return false; }
        return true;
      },
      snap() {
        if (!active) return;
        spring.jump(target.x, target.y);
        land();
      },
    };
    movers.add(mover);
    const go = (x: number, y: number, params: typeof SPRINGS.ghost | typeof SPRINGS.snapHome) => {
      target = { x, y };
      spring.params = params;
      if (reduced) { spring.jump(x, y); last = { x, y }; land(); return; }
      active = true;
      el.style.willChange = 'transform';
      wake(false);
    };
    return {
      to: (x, y) => go(x, y, SPRINGS.ghost),
      home(x, y, done) {
        landed = done ?? null;
        go(x, y, SPRINGS.snapHome);
      },
      jump(x, y) {
        spring.jump(x, y);
        last = target = { x, y };
        active = false;
        write(x, y, 0);
      },
      destroy() {
        movers.delete(mover);
        if (lift) el.classList?.remove('fx-lifted');
      },
    };
  };

  const magnetic = (el: HTMLElement): Unsub => {
    let rx: SpringState = { x: 0, v: 0 }, ry: SpringState = { x: 0, v: 0 };
    let tx = 0, ty = 0, active = false;
    const write = () => {
      el.style.transform = rx.x === 0 && ry.x === 0 ? '' : `perspective(900px) rotateX(${rx.x.toFixed(2)}deg) rotateY(${ry.x.toFixed(2)}deg)`;
    };
    const mover: Mover = {
      step(dt) {
        if (!active) return false;
        rx = springStep(rx, tx, SPRINGS.card, dt);
        ry = springStep(ry, ty, SPRINGS.card, dt);
        if (springSettled(rx, tx, 0.02) && springSettled(ry, ty, 0.02)) {
          rx = { x: tx, v: 0 };
          ry = { x: ty, v: 0 };
          active = false;
          if (tx === 0 && ty === 0) el.style.willChange = '';
        }
        write();
        return active;
      },
      snap() {
        rx = { x: 0, v: 0 };
        ry = { x: 0, v: 0 };
        tx = ty = 0;
        active = false;
        el.style.willChange = '';
        write();
      },
    };
    const onMove = (e: PointerEvent) => {
      if (reduced) return;
      // a layout read in an input handler, never in the frame loop
      const m = magnetTilt(e.clientX, e.clientY, el.getBoundingClientRect());
      tx = m.rx;
      ty = m.ry;
      el.style.setProperty('--fx-mx', `${m.mx.toFixed(1)}%`);
      el.style.setProperty('--fx-my', `${m.my.toFixed(1)}%`);
      el.style.willChange = 'transform';
      active = true;
      wake(false);
    };
    const onLeave = () => {
      tx = ty = 0;
      active = !reduced;
      if (reduced) mover.snap();
      else wake(false);
    };
    el.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerleave', onLeave);
    movers.add(mover);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      movers.delete(mover);
      el.style.transform = '';
      el.style.willChange = '';
    };
  };

  // ---------- the loop ----------

  let inTick = false;
  const wake = (draw = true) => {
    if (draw) needsDraw = true;
    lastActivity = performance.now();
    // inside a tick, the tick itself decides whether to go on (one callback in flight, never two)
    if (inTick) return;
    if (!raf && !destroyed && canvas && !(typeof document !== 'undefined' && document.hidden)) raf = requestAnimationFrame(tick);
  };

  const tick = (t: number) => {
    raf = 0;
    if (destroyed || !renderer) return;
    if (document.hidden) return;
    inTick = true;
    try {
      step(t);
    } finally {
      inTick = false;
    }
  };

  const step = (t: number) => {
    const following = stepMovers(t);
    const moving = camera.tick(t);
    const busy = conductor.busy(t) || moving;
    const animateSea = !reduced && governor.level < 2 && t - lastActivity < IDLE_SLEEP_MS;
    const due = busy || needsDraw || (animateSea && t - lastDraw >= IDLE_FRAME_MS);
    if (due) {
      if (animateSea) seaTime += Math.min(100, t - (lastSea || t)) / 1000;
      lastSea = t;
      const frame = conductor.frame(t);
      frame.camera[CAM.time] = seaTime;
      const cam = camera.get();
      const px = reduced ? 0 : cam.x * cam.zoom * PAN_PARALLAX + cursor.x * PARALLAX_PX;
      const py = reduced ? 0 : cam.y * cam.zoom * PAN_PARALLAX + cursor.y * PARALLAX_PX;
      frame.camera[CAM.parallaxX] = px;
      frame.camera[CAM.parallaxY] = py;
      renderer?.draw(frame);
      shownFrame = frame;
      applyOverlay(frame.overlay);
      // only frames drawn back to back at full rate speak to the governor
      if (busy && lastFull !== null && t - lastFull < 250) {
        const ms = t - lastFull;
        perf.sample(ms);
        const next = governor.sample(ms, t);
        if (next !== null) setLevel(next as Quality);
      }
      lastFull = busy ? t : null;
      lastDraw = t;
      needsDraw = false;
      if (perfHud) perf.show(frame.bitCount, frame.particleCount);
    }
    if (busy || animateSea || needsDraw || following) {
      if (!raf) raf = requestAnimationFrame(tick);
    } else {
      // a sleep is not a slow frame: the governor keeps its window and its good run (it climbs on wall time)
      lastFull = null;
      governor.pause();
      // a long calm climbs a rung back even with nothing animating (a screen after a busy one)
      const up = governor.idle(t);
      if (up !== null) setLevel(up as Quality);
      // asleep until the next deadline (a slip fading out, a trace ending), if there is one; while a rung is
      // down, a look again in 10 s so the calm can climb it back
      const at = conductor.nextWake(t);
      const next = governor.level > 0 ? Math.min(at ?? Infinity, t + CLIMB_AFTER_MS) : at;
      if (wakeTimer) clearTimeout(wakeTimer);
      wakeTimer = next === null || next === Infinity ? null : setTimeout(() => { wakeTimer = null; wake(); }, Math.max(0, next - t));
    }
  };

  // ---------- reduced motion: a cut is a crossfade ----------

  let shownFrame: FrameModel | null = null;   // the frame on screen now
  let fadeAt = -Infinity;
  /**
   * Hold the view on screen over the canvas and fade it out in 150 ms while
   * the new one draws beneath: what a dive, a ring unrolling or a stack
   * exploding becomes under reduced motion (§4.6). The GL buffer is not
   * preserved, so the frame on screen is drawn again and read in this task.
   */
  const crossfade = () => {
    if (!reduced || destroyed || !canvas || !renderer || !shownFrame || !layer) return;
    const t = performance.now();
    if (t - fadeAt < CROSSFADE_MS) return;   // the one running still holds the old view
    fadeAt = t;
    try {
      renderer.draw(shownFrame);
      const vp = camera.viewport();
      const kx = canvas.width / Math.max(1, vp.canvas.width), ky = canvas.height / Math.max(1, vp.canvas.height);
      const W = Math.max(1, Math.round(vp.stage.width * kx)), H = Math.max(1, Math.round(vp.stage.height * ky));
      const snap = document.createElement('canvas');
      snap.width = W;
      snap.height = H;
      const g = snap.getContext('2d');
      if (!g) return;
      g.drawImage(canvas, (vp.stage.left - vp.canvas.left) * kx, (vp.stage.top - vp.canvas.top) * ky, W, H, 0, 0, W, H);
      snap.className = 'fx-crossfade';
      snap.setAttribute('aria-hidden', 'true');
      snap.style.width = `${vp.stage.width}px`;
      snap.style.height = `${vp.stage.height}px`;
      snap.style.transform = `translate3d(${(vp.stage.left - layerOrigin.x).toFixed(1)}px, ${(vp.stage.top - layerOrigin.y).toFixed(1)}px, 0)`;
      layer.prepend(snap);
      const done = () => snap.remove();
      setTimeout(done, CROSSFADE_MS + 250);   // a hidden tab never finishes the animation
      snap.animate([{ opacity: 1 }, { opacity: 0 }], { duration: CROSSFADE_MS, easing: 'linear', fill: 'forwards' }).onfinish = done;
    } catch {
      // no snapshot: a plain cut
    }
    wake();
  };

  // ---------- quality ----------

  const setLevel = (q: Quality) => {
    conductor.setQuality(q);
    camera.setTiltAllowed(q < 4);
    if (q >= 5 && renderer?.kind === 'gl') useRenderer('canvas2d');
    else if (q < 5 && renderer?.kind === 'canvas2d' && glAllowed) useRenderer('gl');
    renderer?.setQuality(q);
    measure();
    eco();
    for (const fn of qualityFns) fn(q);
    wake();
  };

  const freshCanvas = (): HTMLCanvasElement | null => {
    if (!canvas) return null;
    const next = canvas.cloneNode(false) as HTMLCanvasElement;
    canvas.replaceWith(next);
    observeCanvas(next);
    canvas = next;
    return next;
  };

  const useRenderer = (kind: 'gl' | 'canvas2d') => {
    if (!canvas) return;
    const had = renderer;
    if (had) {
      had.destroy();
      renderer = null;
    }
    // a canvas keeps the first context type it was given, so a switch needs a new element
    const target = had ? freshCanvas() : canvas;
    if (!target) return;
    let next: FxRenderer | null = null;
    if (kind === 'gl') {
      const gl = createGlRenderer();
      if (gl.mount(target)) next = gl;
      else {
        glAllowed = false;
        const c2 = freshCanvas();
        const cv = createCanvas2dRenderer();
        if (c2 && cv.mount(c2)) next = cv;
      }
    } else {
      const cv = createCanvas2dRenderer();
      if (cv.mount(target)) next = cv;
    }
    renderer = next;
    if (!renderer) return;
    if (theme) renderer.setTheme(theme);
    renderer.setReduced(reduced);
    renderer.setQuality((renderer.kind === 'canvas2d' ? 5 : Math.min(4, governor.level)) as Quality);
    cleanups.push(renderer.onLost((lost) => {
      if (lost) {
        if (lostTimer) clearTimeout(lostTimer);
        lostTimer = setTimeout(() => {
          lostTimer = null;
          glAllowed = false;
          governor.level = 5;
          setLevel(5);
        }, LOST_GRACE_MS);
      } else {
        if (lostTimer) clearTimeout(lostTimer);
        lostTimer = null;
        conductor.builder.invalidateAll();
        wake();
      }
    }));
    conductor.builder.invalidateAll();
    measure();
  };

  // ---------- geometry ----------

  let ro: ResizeObserver | null = null;
  const observeCanvas = (c: HTMLCanvasElement) => {
    ro?.observe(c);
  };

  const measure = () => {
    if (!canvas || !overlayRoot || !layer) return;
    const cr = canvas.getBoundingClientRect();
    const sr = overlayRoot.getBoundingClientRect();
    const lr = layer.getBoundingClientRect();
    layerOrigin = { x: lr.left, y: lr.top };
    stage = { left: sr.left, top: sr.top, width: sr.width || cr.width, height: sr.height || cr.height };
    camera.setViewport({
      canvas: { left: cr.left, top: cr.top, width: Math.max(1, cr.width), height: Math.max(1, cr.height) },
      stage: { left: stage.left, top: stage.top, width: Math.max(1, stage.width), height: Math.max(1, stage.height) },
    });
    renderer?.resize(Math.max(1, cr.width), Math.max(1, cr.height), dpr());
    conductor.builder.recheck();
    wake();
  };

  // ---------- the overlay: slips, sentences, fix-its ----------

  const els = new Map<string, { el: HTMLElement; content: OverlayContent }>();

  const fire = (f: FixIt, at: Origin, via: 'click' | 'keyboard') => {
    for (const fn of [...fixFns]) fn(f, at, via);
  };

  const render = (id: string, c: OverlayContent): HTMLElement => {
    switch (c.kind) {
      case 'token': {
        const el = h('span', { class: 'fx-token fx-moving' }, c.text);
        el.style.borderColor = c.color;
        return el;
      }
      case 'slip':
        return h('div', { class: 'fx-slip' }, h('span', { class: c.ok ? 'fx-ok' : '' }, c.text));
      case 'latency':
        return h('span', { class: 'fx-latency' }, c.text);
      case 'sentence': {
        // The loser's remedies live here, so the card stays while it has focus or the pointer,
        // Esc dismisses it, and focus that was inside goes back where it came from.
        const detail = h('span', { class: 'fx-detail' });
        let back: HTMLElement | null = null;
        let focused = false, hovered = false;
        const hold = () => {
          conductor.hold(id, focused || hovered);
          wake(false);
        };
        const close = () => {
          if (el.contains(document.activeElement) && back?.isConnected) back.focus();
          conductor.dismiss(id);
          wake();
        };
        const el: HTMLElement = h('div', {
          class: 'fx-sentence', role: 'group', 'aria-label': c.eyebrow, 'data-sentence': id,
          onkeydown: (e: Event) => {
            if ((e as KeyboardEvent).key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            close();
          },
          onfocusin: (e: Event) => {
            const from = (e as FocusEvent).relatedTarget as HTMLElement | null;
            if (!focused && from && typeof from.focus === 'function' && !el.contains(from)) back = from;
            focused = true;
            hold();
          },
          onfocusout: (e: Event) => {
            const to = (e as FocusEvent).relatedTarget as Node | null;
            if (to && el.contains(to)) return;
            focused = false;
            hold();
          },
          onpointerenter: () => { hovered = true; hold(); },
          onpointerleave: () => { hovered = false; hold(); },
        },
          h('span', { class: 'fx-eyebrow' }, c.eyebrow),
          detail,
          // The door's own words stay, small, under the plain ones.
          c.plain ? h('span', { class: 'fx-door' }, `the door: ${c.detail}`) : null,
          c.subtitle ? h('span', { class: 'fx-subtitle' }, c.subtitle) : null,
          c.fixits.length ? h('div', { class: 'fx-fixits', role: 'group', 'aria-label': 'Fixes' }, ...c.fixits.map((f) =>
            h('button', {
              class: 'fx-fixit', type: 'button',
              onclick: (e: Event) => {
                const b = e.currentTarget as HTMLElement;
                const r = b.getBoundingClientRect?.();
                const at: Origin = r && (r.width || r.height) ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
                const via = (e as MouseEvent).detail === 0 ? 'keyboard' : 'click';
                close();
                fire(f, at, via);
              },
            },
              f.label, h('span', { class: 'fx-op' }, f.op)))) : null,
          h('button', { class: 'fx-dismiss', type: 'button', 'aria-label': 'Dismiss (Esc)', onclick: close }, '×'));
        typist.type(detail, c.plain ?? c.detail, 'type');
        return el;
      }
      case 'marker':
        return h('button', {
          class: 'fx-mark', type: 'button', title: `${c.label}: open the reason again`, 'aria-label': `${c.label}: open the reason again`,
          onclick: () => { conductor.reopen(id); wake(); },
        }, '!');
      case 'problem':
        return h('div', { class: 'fx-problem', role: 'status' },
          h('strong', {}, c.title), h('span', {}, c.detail), c.countdown ? h('span', { class: 'fx-countdown' }, c.countdown) : null);
      case 'chip':
        return h('button', { class: 'fx-chip', type: 'button', onclick: () => { conductor.replayAway(); wake(); } }, c.text);
      case 'aim': {
        const el = h('span', { class: 'fx-aim' }, c.text);
        el.style.color = c.color;
        return el;
      }
    }
  };

  /** Update text in place where a slip's words change every frame (latency, countdown). */
  const refresh = (entry: { el: HTMLElement; content: OverlayContent }, c: OverlayContent): boolean => {
    if (entry.content.kind !== c.kind) return false;
    if (c.kind === 'latency') { entry.el.textContent = c.text; return true; }
    if (c.kind === 'problem' && entry.content.kind === 'problem') {
      const cd = entry.el.querySelector('.fx-countdown');
      if (cd && c.countdown) { cd.textContent = c.countdown; return true; }
    }
    return false;
  };

  const applyOverlay = (list: readonly { id: string; x: number; y: number; scale: number; opacity: number }[]) => {
    if (!layer) return;
    const seen = new Set<string>();
    for (const e of list) {
      const content = conductor.content(e.id);
      if (!content) continue;
      seen.add(e.id);
      let entry = els.get(e.id);
      if (!entry || entry.content !== content) {
        if (entry && refresh(entry, content)) entry.content = content;
        else {
          const el = render(e.id, content);
          if (entry) entry.el.replaceWith(el); else layer.append(el);
          // A refusal from the keyboard: its first fix-it (or the sentence) takes focus, one Tab from each fix.
          if (!entry && content.kind === 'sentence' && content.focus && mayTakeFocus()) {
            const first = el.querySelector<HTMLElement>('.fx-fixit') ?? el.querySelector<HTMLElement>('.fx-dismiss');
            first?.focus();
          }
          entry = { el, content };
          els.set(e.id, entry);
        }
      }
      const s = entry.el.style;
      // A sentence or a problem card is centred on its point but kept whole on screen, clear of the HUD and the Wire.
      let x = e.x, y = e.y;
      const half = content.kind === 'sentence' ? 214 : content.kind === 'problem' ? 194 : content.kind === 'slip' || content.kind === 'chip' ? 90 : 0;
      if (half > 0 && typeof innerWidth === 'number') {
        const hw = Math.min(half, innerWidth * 0.4 + 4);
        const left = innerWidth > 900 ? 84 : 12;   // clear of the tool rail on a wide stage
        x = Math.min(Math.max(x, hw + left), Math.max(hw + left, innerWidth - hw - 12));
        if (half > 100) y = Math.min(Math.max(y, 64), Math.max(64, innerHeight - 240));
      }
      s.transform = `translate3d(${(x - layerOrigin.x).toFixed(1)}px, ${(y - layerOrigin.y).toFixed(1)}px, 0) scale(${e.scale})`;
      s.opacity = e.opacity.toFixed(3);
    }
    for (const [id, entry] of els) {
      if (seen.has(id)) continue;
      entry.el.remove();
      els.delete(id);
    }
  };

  // ---------- badges ----------

  let ecoEl: HTMLElement | null = null;
  const eco = () => {
    if (!layer) return;
    const q = renderer?.kind === 'canvas2d' ? 5 : governor.level;
    // A developer's readout: shown with the frame HUD (?perf=1), never to a visitor.
    if (q === 0 || forced || !perfHud) { ecoEl?.remove(); ecoEl = null; return; }
    const text = `eco ${q} · ${LADDER[q]}`;
    if (!ecoEl) {
      ecoEl = h('div', { class: 'fx-eco', 'aria-hidden': 'true' }, text);
      layer.append(ecoEl);
    } else ecoEl.textContent = text;
  };

  const perf = (() => {
    const times: number[] = [];
    let el: HTMLElement | null = null;
    let last = 0;
    return {
      sample(ms: number) {
        times.push(ms);
        if (times.length > 120) times.shift();
      },
      show(bits: number, particles: number) {
        if (!layer) return;
        const t = performance.now();
        if (t - last < 500) return;
        last = t;
        const sorted = [...times].sort((a, b) => a - b);
        const p90v = sorted[Math.floor(sorted.length * 0.9)] ?? 0;
        const avg = times.length ? times.reduce((a, b) => a + b, 0) / times.length : 0;
        const text = [
          `fps ${avg ? (1000 / avg).toFixed(0) : '—'}  p90 ${p90v.toFixed(1)} ms`,
          `level ${governor.level} (${LADDER[governor.level]})  ${renderer?.kind ?? '—'}`,
          `bits ${bits}  particles ${particles}  draws ${renderer?.drawCalls ?? 0}`,
          `backlog ${conductor.backlog}`,
        ].join('\n');
        if (!el) {
          el = h('div', { class: 'fx-perf', 'aria-hidden': 'true' }, text);
          layer.append(el);
        } else el.textContent = text;
      },
    };
  })();

  // ---------- theme, motion, visibility ----------

  const applyTheme = () => {
    theme = readTheme(document.documentElement);
    conductor.setTheme(theme);
    renderer?.setTheme(theme);
    wake();
  };

  const setReduced = (on: boolean) => {
    reduced = on;
    conductor.setReduced(on);
    camera.setReduced(on);
    renderer?.setReduced(on);
    if (on) {
      typist.cancelAll();
      for (const m of movers) m.snap();
    }
    try { document.documentElement.dataset.motion = on ? 'reduced' : 'full'; } catch { /* no document */ }
    wake();
  };

  const onVisibility = () => {
    const hidden = document.hidden;
    conductor.setHidden(hidden);
    sound.setHidden(hidden);
    if (hidden) {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      governor.reset();
      lastFull = null;
    } else wake();
  };

  // ---------- the public object ----------

  const facade: Conductor = {
    play(cue: Cue) {
      conductor.play(cue);
      wake(false);
    },
    frame: (now: number) => conductor.frame(now),
    snap() {
      conductor.snap();
      wake();
    },
    setReduced,
    get backlog() {
      return conductor.backlog;
    },
  };

  const fx: FxImpl = {
    conductor: facade,
    origins,
    camera,
    mount(c, overlay) {
      if (destroyed) return;
      canvas = c;
      overlayRoot = overlay;
      const sw = typeof location !== 'undefined' ? readSwitches(location) : { gl0: false, perf: false };
      if (sw.gl0) { glAllowed = false; forced = true; }
      if (sw.perf) perfHud = true;
      layer = h('div', { class: 'fx-layer' });
      overlay.append(layer);
      theme = readTheme(document.documentElement);
      conductor.setTheme(theme);
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => measure());
        ro.observe(c);
        ro.observe(overlay);
        cleanups.push(() => ro?.disconnect());
      }
      useRenderer(glAllowed ? 'gl' : 'canvas2d');
      if (renderer?.kind === 'canvas2d') governor.level = 5;
      const onScroll = () => measure();
      addEventListener('resize', onScroll, { passive: true });
      addEventListener('scroll', onScroll, { passive: true, capture: true });
      cleanups.push(() => { removeEventListener('resize', onScroll); removeEventListener('scroll', onScroll, { capture: true }); });
      const onPointer = (e: PointerEvent) => {
        const w = innerWidth || 1, hh = innerHeight || 1;
        cursor = { x: (e.clientX / w) * 2 - 1, y: (e.clientY / hh) * 2 - 1 };
        if (!reduced && governor.level < 2) wake(false);
      };
      addEventListener('pointermove', onPointer, { passive: true });
      cleanups.push(() => removeEventListener('pointermove', onPointer));
      document.addEventListener('visibilitychange', onVisibility);
      cleanups.push(() => document.removeEventListener('visibilitychange', onVisibility));
      const mo = new MutationObserver(applyTheme);
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
      cleanups.push(() => mo.disconnect());
      const rm = matchMedia('(prefers-reduced-motion: reduce)');
      const onMotion = () => { if (!reducedByUser) setReduced(rm.matches); };
      rm.addEventListener('change', onMotion);
      cleanups.push(() => rm.removeEventListener('change', onMotion));
      cleanups.push(camera.on(() => wake(false)));
      cleanups.push(camera.onCut(crossfade));
      // sound restored from a saved choice made its context before any gesture: the first one starts it
      const unlock = () => sound.unlock();
      addEventListener('pointerdown', unlock, { capture: true, passive: true });
      // A press anywhere but on a refusal's sentence folds it to its mark: it never sits over the field eating presses.
      const fold = (e: Event) => {
        const at = e.target as Node | null;
        for (const [id, entry] of els) {
          if (entry.content.kind !== 'sentence' || (at && entry.el.contains(at))) continue;
          conductor.collapse(id);
          wake();
        }
      };
      addEventListener('pointerdown', fold, { capture: true, passive: true });
      cleanups.push(() => removeEventListener('pointerdown', fold, { capture: true }));
      addEventListener('keydown', unlock, { capture: true, passive: true });
      cleanups.push(() => { removeEventListener('pointerdown', unlock, { capture: true }); removeEventListener('keydown', unlock, { capture: true }); });
      sound.setOn(soundWanted);
      sound.setHidden(document.hidden);
      conductor.setHidden(document.hidden);
      setReduced(reduced);
      measure();
      eco();
      wake();
    },
    setScene(scene: FxScene) {
      // rows that jump (a ring unrolled, a stack exploded) crossfade under reduced motion
      if (conductor.setScene(scene) !== 'same') crossfade();
      wake();
    },
    setReduced(on: boolean) {
      reducedByUser = true;
      setReduced(on);
    },
    setSound(on: boolean) {
      soundWanted = on;
      sound.setOn(on);
    },
    get quality() {
      return renderer?.kind === 'canvas2d' ? 5 : governor.level;
    },
    onQuality(fn) {
      qualityFns.add(fn);
      return () => qualityFns.delete(fn);
    },
    typeText(el, text, how) {
      typist.type(el, text, how);
    },
    ghost,
    magnetic,
    opening() {
      conductor.opening();
      wake();
    },
    setPeer(sid, info) {
      conductor.setPeer(sid, { name: info.name, hue: info.color ? viewerHue(info.color) : undefined });
      wake();
    },
    forgetPeer(sid) {
      conductor.forgetPeer(sid);
      wake();
    },
    onFixIt(fn) {
      fixFns.add(fn);
      return () => { fixFns.delete(fn); };
    },
    chooseFixIt: (f, at, via) => fire(f, at, via),
    get renderer() {
      return renderer?.kind ?? null;
    },
    destroy() {
      destroyed = true;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      if (lostTimer) clearTimeout(lostTimer);
      if (wakeTimer) clearTimeout(wakeTimer);
      for (const fn of cleanups.splice(0)) fn();
      typist.cancelAll();
      for (const m of movers) m.snap();
      movers.clear();
      renderer?.destroy();
      renderer = null;
      shownFrame = null;
      sound.destroy();
      layer?.remove();
      layer = null;
      els.clear();
      qualityFns.clear();
      fixFns.clear();
    },
  };
  return fx;
};
